import { useState, useEffect } from "react";
import { usePermissions } from "@/hooks/usePermissions";
import { useAuth } from "./useAuth";

// Timeout durations in milliseconds
// Both admin and regular users are logged out after 8 hours of inactivity
const ADMIN_TIMEOUT = 8 * 60 * 60 * 1000; // 8 hours
const USER_TIMEOUT = 8 * 60 * 60 * 1000; // 8 hours
const WARNING_BEFORE_TIMEOUT = 60 * 1000; // 1 minute

export function useSessionTimeout() {
  const { isAdmin } = usePermissions();
  const { signOut } = useAuth();
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [showWarning, setShowWarning] = useState(false);

  // Determine timeout based on user role
  const timeoutDuration = isAdmin ? ADMIN_TIMEOUT : USER_TIMEOUT;

  const resetTimeout = () => {
    setLastActivity(Date.now());
    setShowWarning(false);
  };

  useEffect(() => {
    const handleActivity = () => {
      if (!showWarning) {
        setLastActivity(Date.now());
      }
    };

    // Track user activity
    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("keydown", handleActivity);
    window.addEventListener("click", handleActivity);
    window.addEventListener("scroll", handleActivity);

    const checkInterval = setInterval(() => {
      const currentTime = Date.now();
      const timeSinceLastActivity = currentTime - lastActivity;

      // Show warning before timeout
      if (timeSinceLastActivity > timeoutDuration - WARNING_BEFORE_TIMEOUT && !showWarning) {
        setShowWarning(true);
      }

      if (timeSinceLastActivity > timeoutDuration) {
        signOut();
        clearInterval(checkInterval);
      }
    }, 5000); // Check every 5 seconds

    return () => {
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("keydown", handleActivity);
      window.removeEventListener("click", handleActivity);
      window.removeEventListener("scroll", handleActivity);
      clearInterval(checkInterval);
    };
  }, [lastActivity, showWarning, timeoutDuration, signOut]);

  return { showWarning, resetTimeout };
}
